import React, { useContext, useRef, useState } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { Header, SectionWrapper, Title } from './ChartsList.styles';
import {
	ApButtonIcon,
	ApButtonMain,
	ApLoaderDefault,
	ApLoaderText,
	ApMenu,
	ApMenuItem,
} from '@alixpartners/ui-components';
import { EXPORT_OPTIONS } from 'CategoryManagement/utils';
import { useSpendByMonthContext } from 'contexts/CategoryDashboardCharts/SpendByMonthProvider';
import {
	DateRangeWrapper,
	FilterWrapper,
	LoadingContainer,
	NoData,
} from './SpendByMonth.styles';
import { isNull } from 'lodash';
import { getFiltersForSpendByMonth } from 'utils/apiRequests';
import MultiSelect from 'CategoryManagement/Components/MultiSelect/MultiSelect';
import { CategoryManagementContext } from 'contexts/CategoryManagementContext';
import MonthPicker from 'Settings/FirstUsageSettings/MonthPicker';

const SpendByMonth = ({ chartTitle }) => {
	const {
		isLoading,
		options,
		filterData,
		setFilterData,
		applyFilter,
		isFilterApplied,
		handleDateRangeChange,
	} = useSpendByMonthContext();
	const { isDirect, isSourceable, taxonomyType } = useContext(
		CategoryManagementContext
	);

	const { series } = options;
	const chartRef = useRef();
	const [isLoadingOptions, setIsLoadingOptions] = useState(false);

	const checkAndFormat = data => {
		return data.filter(x => x !== null).map(x => {
			return { label: x, value: x };
		});
	};

	const getChartInstance = () => {
		return chartRef && chartRef.current && chartRef.current.chart;
	};

	const handleChartExport = option => {
		const chartInstance = getChartInstance();
		if (chartInstance && option.value === 'png') {
			chartInstance.exportChart(
				{
					filename: chartTitle,
				},
				{
					chart: { width: 1200 },
				}
			);
		}
	};

	const getOptions = async bodyParam => {
		let requestBody = {
			isSourceable: isSourceable,
			aptaxonomy: isDirect,
			taxonomyType: taxonomyType,
			...bodyParam,
		};
		const response = await getFiltersForSpendByMonth(requestBody);
		if (response.succeeded) {
			if (!isNull(response.data)) {
				if (Object.keys(response.data).length > 0) {
					return response.data;
				}
			}
		}
		return null;
	};

	const payloadKeys = {
		category_1: 'category1',
		category_2: 'category2',
		category_3: 'category3',
		category_4: 'category4',
		harmonized_vendor: 'harmonizeD_VENDOR_NAME',
	};

	const onCategory1ChangeHandler = async (key, value) => {
		setFilterData(prevState => ({
			...prevState,
			[key]: {
				...prevState[key],
				selectedOption: value,
			},
		}));
		const newFilters = {
			category1: filterData.category_1.selectedOption,
			[payloadKeys[key]]: value,
		};
		setIsLoadingOptions(true);
		const optionsData = await getOptions(newFilters);
		const formatedCategory2Options = checkAndFormat(optionsData.category2);
		const formatedCategory3Options = checkAndFormat(optionsData.category3);
		const formatedCategory4Options = checkAndFormat(optionsData.category4);
		const formatedHarmonizedVendorOptions = checkAndFormat(
			optionsData.harmonizeD_VENDOR_NAME
		);
		setFilterData(prevState => ({
			...prevState,
			category_2: {
				options: formatedCategory2Options,
				selectedOption:
					formatedCategory2Options.length === 1
						? formatedCategory2Options.map(x => x.value)
						: [],
			},
			category_3: {
				options: formatedCategory3Options,
				selectedOption:
					formatedCategory3Options.length === 1
						? formatedCategory3Options.map(x => x.value)
						: [],
			},
			category_4: {
				options: formatedCategory4Options,
				selectedOption:
					formatedCategory4Options.length === 1
						? formatedCategory4Options.map(x => x.value)
						: [],
			},
			harmonized_vendor: {
				options: formatedHarmonizedVendorOptions,
				selectedOption:
					formatedHarmonizedVendorOptions.length === 1
						? formatedHarmonizedVendorOptions.map(x => x.value)
						: [],
			},
		}));
		setIsLoadingOptions(false);
	};
	const onCategory2ChangeHandler = async (key, value) => {
		setFilterData(prevState => ({
			...prevState,
			[key]: {
				...prevState[key],
				selectedOption: value,
			},
		}));
		const newFilters = {
			category1: filterData.category_1.selectedOption,
			category2: filterData.category_2.selectedOption,
			[payloadKeys[key]]: value,
		};
		setIsLoadingOptions(true);
		const optionsData = await getOptions(newFilters);
		const formatedCategory3Options = checkAndFormat(optionsData.category3);
		const formatedCategory4Options = checkAndFormat(optionsData.category4);
		const formatedHarmonizedVendorOptions = checkAndFormat(
			optionsData.harmonizeD_VENDOR_NAME
		);
		setFilterData(prevState => ({
			...prevState,
			category_3: {
				options: formatedCategory3Options,
				selectedOption:
					formatedCategory3Options.length === 1
						? formatedCategory3Options.map(x => x.value)
						: [],
			},
			category_4: {
				options: formatedCategory4Options,
				selectedOption:
					formatedCategory4Options.length === 1
						? formatedCategory4Options.map(x => x.value)
						: [],
			},
			harmonized_vendor: {
				options: formatedHarmonizedVendorOptions,
				selectedOption:
					formatedHarmonizedVendorOptions.length === 1
						? formatedHarmonizedVendorOptions.map(x => x.value)
						: [],
			},
		}));
		setIsLoadingOptions(false);
	};
	const onCategory3ChangeHandler = async (key, value) => {
		setFilterData(prevState => ({
			...prevState,
			[key]: {
				...prevState[key],
				selectedOption: value,
			},
		}));
		const newFilters = {
			category1: filterData.category_1.selectedOption,
			category2: filterData.category_2.selectedOption,
			category3: filterData.category_3.selectedOption,
			[payloadKeys[key]]: value,
		};
		setIsLoadingOptions(true);
		const optionsData = await getOptions(newFilters);
		const formatedCategory4Options = checkAndFormat(optionsData.category4);
		const formatedHarmonizedVendorOptions = checkAndFormat(
			optionsData.harmonizeD_VENDOR_NAME
		);
		setFilterData(prevState => ({
			...prevState,
			category_4: {
				options: formatedCategory4Options,
				selectedOption:
					formatedCategory4Options.length === 1
						? formatedCategory4Options.map(x => x.value)
						: [],
			},
			harmonized_vendor: {
				options: formatedHarmonizedVendorOptions,
				selectedOption:
					formatedHarmonizedVendorOptions.length === 1
						? formatedHarmonizedVendorOptions.map(x => x.value)
						: [],
			},
		}));
		setIsLoadingOptions(false);
	};

	const onCategory4ChangeHandler = async (key, value) => {
		setFilterData(prevState => ({
			...prevState,
			[key]: {
				...prevState[key],
				selectedOption: value,
			},
		}));
		const newFilters = {
			category1: filterData.category_1.selectedOption,
			category2: filterData.category_2.selectedOption,
			category3: filterData.category_3.selectedOption,
			category4: filterData.category_4.selectedOption,
			[payloadKeys[key]]: value,
		};
		setIsLoadingOptions(true);
		const optionsData = await getOptions(newFilters);
		const formatedHarmonizedVendorOptions = checkAndFormat(
			optionsData.harmonizeD_VENDOR_NAME
		);
		setFilterData(prevState => ({
			...prevState,
			harmonized_vendor: {
				options: formatedHarmonizedVendorOptions,
				selectedOption:
					formatedHarmonizedVendorOptions.length === 1
						? formatedHarmonizedVendorOptions.map(x => x.value)
						: [],
			},
		}));
		setIsLoadingOptions(false);
	};

	return (
		<SectionWrapper>
			<Header>
				<Title>{chartTitle}</Title>
				<ApMenu button={<ApButtonIcon iconName="download" />}>
					{EXPORT_OPTIONS.map(option => (
						<ApMenuItem
							key={option.value}
							onClick={() => handleChartExport(option)}
						>
							{option.label}
						</ApMenuItem>
					))}
				</ApMenu>
			</Header>
			<FilterWrapper>
				<MultiSelect
					name="category_1"
					label="Category 1"
					options={filterData.category_1.options}
					selected={filterData.category_1.selectedOption}
					isLoading={isLoadingOptions}
					onChange={v => {
						onCategory1ChangeHandler('category_1', v);
					}}
				/>
				<MultiSelect
					name="category_2"
					label="Category 2"
					options={filterData.category_2.options}
					selected={filterData.category_2.selectedOption}
					isLoading={isLoadingOptions}
					disabled={
						!filterData.category_1.selectedOption?.length > 0 ||
						isLoadingOptions
					}
					onChange={v => {
						onCategory2ChangeHandler('category_2', v);
					}}
				/>
				<MultiSelect
					name="category_3"
					label="Category 3"
					options={filterData.category_3.options}
					selected={filterData.category_3.selectedOption}
					isLoading={isLoadingOptions}
					disabled={
						!filterData.category_2.selectedOption?.length > 0 ||
						isLoadingOptions
					}
					onChange={v => {
						onCategory3ChangeHandler('category_3', v);
					}}
				/>
				<MultiSelect
					name="category_4"
					label="Category 4"
					options={filterData.category_4.options}
					selected={filterData.category_4.selectedOption}
					isLoading={isLoadingOptions}
					disabled={
						!filterData.category_3.selectedOption?.length > 0 ||
						isLoadingOptions
					}
					onChange={v => {
						onCategory4ChangeHandler('category_4', v);
					}}
				/>
				<MultiSelect
					name="vendor"
					label="Vendor"
					options={filterData.harmonized_vendor.options}
					selected={filterData.harmonized_vendor.selectedOption}
					isLoading={isLoadingOptions}
					disabled={
						(!filterData.category_1.selectedOption?.length > 0 &&
							!filterData.category_2.selectedOption?.length > 0 &&
							!filterData.category_3.selectedOption?.length > 0 &&
							!filterData.category_4.selectedOption?.length > 0) ||
						isLoadingOptions
					}
					onChange={v => {
						setFilterData(prevState => ({
							...prevState,
							harmonized_vendor: {
								...prevState.harmonized_vendor,
								selectedOption: v,
							},
						}));
					}}
				/>
				<DateRangeWrapper>
					<MonthPicker
						srcRange={filterData.dateRange.srcRange}
						stateRange={filterData.dateRange.stateRange}
						onConfirm={handleDateRangeChange}
					/>
				</DateRangeWrapper>

				{/* <ApSimpleDropdown
						options={DirectIndirectOptions}
						selected={
							DirectIndirectOptions.filter(x => x.value === isDirect)?.[0]
						}
						onChange={e => handleDirectIndirectChange(e.value)}
					/> */}

				<ApButtonMain
					onClick={applyFilter}
					disabled={
						!filterData.harmonized_vendor.selectedOption?.length > 0 ||
						isLoadingOptions
					}
					small
				>
					Apply Filters
				</ApButtonMain>
			</FilterWrapper>
			{isLoading ? (
				<LoadingContainer>
					<ApLoaderDefault loaderText={() => <ApLoaderText />} />
				</LoadingContainer>
			) : (
				<>
					{series.length === 0 ? (
						<NoData>
							{!isFilterApplied
								? 'Please select a filter to view the data'
								: 'No data available'}
						</NoData>
					) : (
						<HighchartsReact
							highcharts={Highcharts}
							options={options}
							ref={chartRef}
						/>
					)}
				</>
			)}
		</SectionWrapper>
	);
};

export default SpendByMonth;
